import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { useFieldValue } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { SelectableValue } from "../models/common/selectable-value";
import { ControlState } from "../models/flexible/control-state";
import { FlexControlProps } from "../models/flexible/flex-field-controller";
import { CoverageRequestRating, CoverageRequestTherapyDecision, Decision, Maybe } from "../models/generated/graphql";
import { renderRequiredMessage } from "../utils/render-required-message";
import { TrFun } from "../utils/types";
import { GeneralFieldController } from "./general-field-controller";
import { getEnumValues } from "../utils/get-enum-values";

export class RatingFieldController extends GeneralFieldController {
	override createValidationRule(_initialData: unknown, translate: TrFun): Yup.AnySchema {
		return super
			.createFieldValidationRuleByDataType(translate)
			.nullable()
			.when(
				["isRatingCondition", "decisionTherapy"],
				(isRatingCondition: boolean, decisionTherapy: CoverageRequestTherapyDecision, schema: Yup.AnySchema) =>
					(isRatingCondition || this.parent.context.isNegotiable) && decisionTherapy !== CoverageRequestTherapyDecision.Declined
						? schema.required(renderRequiredMessage(this.schema.attribute.identifier, translate))
						: schema
			);
	}

	override useControlState(): ControlState {
		const [isRatingCondition] = useFieldValue("isRatingCondition");
		const [decision] = useFieldValue<Decision>(AttributeIdentifiers.DecisionTherapy);

		return (isRatingCondition || this.parent.context.isNegotiable) && decision !== CoverageRequestTherapyDecision.Declined
			? "enabled"
			: "hidden";
	}

	override useControlProps(): Maybe<FlexControlProps> | undefined {
		const { t } = useTranslation();

		const ratingOptions = getEnumValues(CoverageRequestRating).map((v) => ({ label: t(v), value: v })) as SelectableValue[];
		const [decision] = useFieldValue<Decision>(AttributeIdentifiers.DecisionTherapy);

		function filterRatingOptions(options: Array<SelectableValue>): Array<SelectableValue> {
			switch (decision) {
				case CoverageRequestTherapyDecision.Approved:
					return options.filter(
						(o) =>
							o.value === CoverageRequestRating.A ||
							o.value === CoverageRequestRating.B ||
							o.value === CoverageRequestRating.Ab ||
							o.value === CoverageRequestRating.C ||
							o.value === CoverageRequestRating.NoRating
					);
				case CoverageRequestTherapyDecision.Trial:
					return options.filter((o) => o.value === CoverageRequestRating.C || o.value === CoverageRequestRating.NoRating);
				default:
					return options;
			}
		}

		return {
			controlType: "typeahead",
			options: filterRatingOptions(ratingOptions || []),
			property: "value",
			selectPropertyValue: true,
		};
	}
}
